import React, { useRef, useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Webcam } from "./Webcam";
import { PhotoConfirmation } from "./PhotoConfirmation";
import { Header } from "./Header";
import { ReactComponent as CameraSvg } from "../svgs/camera.svg";
import { ReactComponent as GuidanceIconSvg } from "../svgs/guidance-icon.svg";
import { PhotoGuidance } from "./PhotoGuidance";
import {
  WarehouseButtonsFooter,
  WarehouseButton,
  WarehouseButtonGroup,
} from "./WarehouseButtonsFooter";

export const TakePhoto = ({
  title,
  description,
  onCancel,
  onPhotoTaken,
  retakeButtonText,
  showGuidance = false,
  bucketPlaceholder = null,
}) => {
  const webcamRef = useRef(null);
  const takePhotoButtonRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState("");
  const [showGuidanceModal, setShowGuidanceModal] = useState(false);

  const capturePhoto = useCallback(() => {
    const photo = webcamRef.current?.getScreenshot();
    if (photo) {
      setCapturedPhoto(photo);
      setShowConfirmation(true);
    }
  }, []);

  const handleConfirmPhoto = useCallback(() => {
    onPhotoTaken(capturedPhoto);
    setShowConfirmation(false);
    setCapturedPhoto("");
  }, [capturedPhoto, onPhotoTaken]);

  const handleCancelPhoto = useCallback(() => {
    setShowConfirmation(false);
    setCapturedPhoto("");
  }, []);

  // Handle Enter key to trigger photo capture
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Enter" && !isLoading) {
        e.preventDefault();
        if (showConfirmation) {
          handleConfirmPhoto();
        } else {
          capturePhoto();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isLoading, showConfirmation, handleConfirmPhoto, capturePhoto]);

  return (
    <div className="z-50 fixed inset-0 h-full w-full">
      <div className="relative bg-[#030711] h-full flex flex-col">
        <Webcam ref={webcamRef} setIsLoading={setIsLoading} />
        <Header opacity="100%" isCameraActive>
          <div className="flex justify-between items-center w-full">
            <div className="flex-1" />
            <div className="flex flex-col items-center gap-1">
              <span className="font-bold text-2xl text-white">{title}</span>
              {description && (
                <span className="text-sm text-white">{description}</span>
              )}
            </div>
            <div className="flex-1 flex justify-end pr-6">
              {showGuidance && (
                <button
                  onClick={() => setShowGuidanceModal(true)}
                  className="hover:opacity-80 transition-opacity"
                >
                  <GuidanceIconSvg className="w-8 h-8 pulsate-white" />
                </button>
              )}
            </div>
          </div>
        </Header>

        <div className="absolute bottom-0 left-0 right-0">
          <WarehouseButtonsFooter isCameraActive>
            <WarehouseButtonGroup columns={2}>
              <button
                onClick={() => {
                  onCancel();
                }}
                className="w-full rounded-lg font-normal py-3.5 m-0 border border-transparent bg-white text-black hover:bg-gray-200"
              >
                Back
              </button>
              <WarehouseButton
                ref={takePhotoButtonRef}
                variant="primary"
                onClick={capturePhoto}
                disabled={isLoading}
              >
                <div className="flex items-center justify-center gap-2">
                  <CameraSvg className="fill-white w-5 h-5" />
                  <span>Take Photo</span>
                </div>
              </WarehouseButton>
            </WarehouseButtonGroup>
          </WarehouseButtonsFooter>
        </div>
      </div>
      {showConfirmation &&
        createPortal(
          <PhotoConfirmation
            title={title}
            photo={capturedPhoto}
            onConfirm={handleConfirmPhoto}
            onCancel={handleCancelPhoto}
            retakeButtonText={retakeButtonText}
          />,
          document.body
        )}
      {showGuidanceModal && (
        <PhotoGuidance
          onClose={() => setShowGuidanceModal(false)}
          currentStep="Sort Contents"
          bucketTitle={title}
          bucketPlaceholder={bucketPlaceholder}
        />
      )}
    </div>
  );
};
